'use strict';

/**
 * ServiceWorkerHealthMonitor - Monitors service worker health during exam sessions.
 *
 * This service:
 * - Tracks the SW instanceId to detect SW replacements
 * - Monitors for controllerchange events after login
 * - Performs periodic health check pings
 * - Validates SW state (instanceId, loaderInitialized, isAuthenticated, userUUID)
 * - Triggers recovery on failure via page reload (SessionResumeService handles restoration)
 */
export class ServiceWorkerHealthMonitor {
    static $inject = [
        'PWAService',
        '$signals',
        '$interval',
        '$timeout',
        'TelemetryBeaconService'
    ];

    constructor(PWAService, $signals, $interval, $timeout, TelemetryBeaconService) {
        this.PWAService = PWAService;
        this.$signals = $signals;
        this.$interval = $interval;
        this.$timeout = $timeout;
        this.TelemetryBeaconService = TelemetryBeaconService;

        this.isMonitoring = false;
        this.healthCheckInterval = null;
        this.expectedInstanceId = null;
        this.expectedUserUUID = null;
        this.controllerChangeListener = null;

        // Health check configuration
        this.HEALTH_CHECK_INTERVAL_MS = 30000; // 30 seconds
        this.HEALTH_CHECK_TIMEOUT_MS = 5000;   // 5 second timeout for each check
        this.MAX_RECOVERY_ATTEMPTS = 3;        // Max recovery attempts before giving up
        this.isCheckInProgress = false;         // Debounce guard for concurrent checks
    }

    /**
     * Start monitoring after successful login.
     * Captures the initial SW instanceId and sets up listeners.
     * @param {string} userUUID - The authenticated user's UUID
     */
    async startMonitoring(userUUID) {
        if (this.isMonitoring) {
            console.log('ServiceWorkerHealthMonitor: Already monitoring');
            return;
        }

        console.log('ServiceWorkerHealthMonitor: Starting monitoring for user', userUUID);
        this.expectedUserUUID = userUUID;
        this.isMonitoring = true;
        this.hasPassedFirstCheck = false;

        // Get initial SW status to capture instanceId (with retry)
        const maxRetries = 3;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const status = await this.PWAService.checkServiceWorkerStatus();
                this.expectedInstanceId = status.instanceId;
                this.TelemetryBeaconService?.setSwInstanceId(status.instanceId);
                console.log('ServiceWorkerHealthMonitor: Captured initial instanceId:', this.expectedInstanceId);
                break;
            } catch (error) {
                console.error('ServiceWorkerHealthMonitor: Failed to get initial status (attempt', attempt + '/' + maxRetries + '):', error.message);
                if (attempt < maxRetries) {
                    await new Promise(resolve => this.$timeout(resolve, 1000));
                } else {
                    console.error('ServiceWorkerHealthMonitor: Could not capture initial instanceId after', maxRetries, 'attempts - monitoring will be limited (cannot detect SW replacement by instanceId)');
                }
            }
        }

        // Guard: if stopMonitoring() was called during the retry loop, bail out
        if (!this.isMonitoring) {
            console.log('ServiceWorkerHealthMonitor: Monitoring was stopped during startup, aborting');
            return;
        }

        // Set up controllerchange listener to detect SW replacement
        this.setupControllerChangeListener();

        // Start periodic health checks
        this.healthCheckInterval = this.$interval(() => {
            this.performHealthCheck();
        }, this.HEALTH_CHECK_INTERVAL_MS);

        console.log('ServiceWorkerHealthMonitor: Monitoring started with expectedInstanceId:', this.expectedInstanceId || 'NONE (degraded mode)');
    }

    /**
     * Stop monitoring (called on logout or exam end).
     * Cleans up listeners and intervals.
     */
    stopMonitoring() {
        if (!this.isMonitoring) {
            return;
        }

        this.isMonitoring = false;

        if (this.healthCheckInterval) {
            this.$interval.cancel(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }

        if (this.controllerChangeListener && navigator.serviceWorker) {
            navigator.serviceWorker.removeEventListener('controllerchange', this.controllerChangeListener);
            this.controllerChangeListener = null;
        }

        this.expectedInstanceId = null;
        this.expectedUserUUID = null;
        console.log('ServiceWorkerHealthMonitor: Monitoring stopped');
    }

    /**
     * Set up listener for service worker controller changes.
     * A controller change during an active session is suspicious and triggers
     * an immediate health check.
     */
    setupControllerChangeListener() {
        if (!navigator.serviceWorker) return;

        this.controllerChangeListener = () => {
            console.warn('ServiceWorkerHealthMonitor: Controller changed during session!');
            // Controller changed - this is suspicious during an exam
            // Perform immediate health check
            this.performHealthCheck(true);
        };

        navigator.serviceWorker.addEventListener('controllerchange', this.controllerChangeListener);
    }

    /**
     * Perform a health check by querying the service worker status.
     * @param {boolean} immediate - If true, triggered by controllerchange (higher priority)
     */
    async performHealthCheck(immediate = false) {
        if (!this.isMonitoring) return;
        if (this.isCheckInProgress) {
            console.debug('ServiceWorkerHealthMonitor: Check already in progress, skipping');
            return;
        }

        this.isCheckInProgress = true;
        try {
            if (immediate) {
                console.log('ServiceWorkerHealthMonitor: Immediate health check triggered (controller changed)');
            } else {
                console.debug('ServiceWorkerHealthMonitor: Periodic health check');
            }

            const status = await this.checkServiceWorkerStatusWithTimeout();

            const validationResult = this.validateStatus(status);

            if (!validationResult.isHealthy) {
                console.error('ServiceWorkerHealthMonitor: Health check FAILED -', validationResult.reason);
                console.error('ServiceWorkerHealthMonitor: Expected:', { instanceId: this.expectedInstanceId, userUUID: this.expectedUserUUID });
                console.error('ServiceWorkerHealthMonitor: Actual:', status);
                this.triggerRecovery(validationResult.reason);
            } else {
                // Clear recovery counter only after first passing check confirms SW is healthy
                if (!this.hasPassedFirstCheck) {
                    this.hasPassedFirstCheck = true;
                    sessionStorage.removeItem('healthCheckRecoveryAttempts');
                }
                console.debug('ServiceWorkerHealthMonitor: Health check passed');
            }
        } catch (error) {
            console.error('ServiceWorkerHealthMonitor: Health check error:', error.message || error);
            this.triggerRecovery('Health check timed out or errored: ' + (error.message || error));
        } finally {
            this.isCheckInProgress = false;
        }
    }

    /**
     * Check SW status with timeout to prevent hanging.
     * @returns {Promise<Object>} - The status object from the service worker
     */
    checkServiceWorkerStatusWithTimeout() {
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Health check timeout')), this.HEALTH_CHECK_TIMEOUT_MS);
        });
        return Promise.race([
            this.PWAService.checkServiceWorkerStatus(),
            timeoutPromise
        ]);
    }

    /**
     * Validate the SW status response against expected values.
     * @param {Object} status - The status from checkServiceWorkerStatus
     * @returns {{isHealthy: boolean, reason: string|null}} - Validation result
     */
    validateStatus(status) {
        // Check instanceId matches (if we have one captured)
        if (this.expectedInstanceId && status.instanceId !== this.expectedInstanceId) {
            return {
                isHealthy: false,
                reason: `instanceId mismatch: expected ${this.expectedInstanceId}, got ${status.instanceId}`
            };
        }

        // Check loader is initialized
        if (!status.loaderInitialized) {
            return {
                isHealthy: false,
                reason: 'Assessment loader not initialized'
            };
        }

        // Check authentication
        if (!status.isAuthenticated) {
            return {
                isHealthy: false,
                reason: 'Service worker reports not authenticated'
            };
        }

        // Check userUUID matches (if we have one)
        if (this.expectedUserUUID && status.userUUID !== this.expectedUserUUID) {
            return {
                isHealthy: false,
                reason: `userUUID mismatch: expected ${this.expectedUserUUID}, got ${status.userUUID}`
            };
        }

        return { isHealthy: true, reason: null };
    }

    /**
     * Trigger recovery via page reload.
     * Sets a flag so that session resume data is preserved during the reset.
     * @param {string} reason - Reason for recovery (for logging)
     */
    triggerRecovery(reason) {
        // Guard against infinite recovery loops
        const attemptCount = parseInt(sessionStorage.getItem('healthCheckRecoveryAttempts') || '0', 10);
        if (attemptCount >= this.MAX_RECOVERY_ATTEMPTS) {
            console.error('ServiceWorkerHealthMonitor: ========== RECOVERY LIMIT REACHED ==========');
            console.error('ServiceWorkerHealthMonitor: Already attempted', attemptCount, 'recoveries without success');
            console.error('ServiceWorkerHealthMonitor: Reason for latest failure:', reason);
            console.error('ServiceWorkerHealthMonitor: Stopping monitoring - manual intervention required');
            this.stopMonitoring();
            this.$signals.emit('serviceWorkerHealthCheckFailed', { reason, recoveryExhausted: true });
            return;
        }

        console.error('ServiceWorkerHealthMonitor: ========== TRIGGERING RECOVERY ==========');
        console.error('ServiceWorkerHealthMonitor: Reason:', reason);
        console.error('ServiceWorkerHealthMonitor: Recovery attempt', attemptCount + 1, 'of', this.MAX_RECOVERY_ATTEMPTS);
        console.error('ServiceWorkerHealthMonitor: Expected state:', {
            instanceId: this.expectedInstanceId,
            userUUID: this.expectedUserUUID,
            sessionResumeKey: sessionStorage.getItem('sessionResumeKey') ? 'yes' : 'no',
            sessionResumeAssessmentId: localStorage.getItem('sessionResumeAssessmentId') || 'none'
        });

        // Stop monitoring to prevent concurrent triggers
        this.stopMonitoring();

        // Emit signal so UI can show feedback if desired
        this.$signals.emit('serviceWorkerHealthCheckFailed', { reason });

        // Increment recovery counter and set recovery flag
        // The 'healthCheckRecovery' flag tells PWAAuthenticationBridgeClient to preserve session data
        sessionStorage.setItem('healthCheckRecoveryAttempts', (attemptCount + 1).toString());
        sessionStorage.setItem('healthCheckRecovery', 'true');

        // Small delay to let any UI updates happen, then reload
        this.$timeout(() => {
            console.log('ServiceWorkerHealthMonitor: Reloading page for recovery...');
            window.location.reload();
        }, 500);
    }

    /**
     * Check if monitoring is currently active.
     * @returns {boolean}
     */
    isActive() {
        return this.isMonitoring;
    }
}
